"use client";

/**
 * useWalletActionGuard — shared pre-action validation layer for all wallet-dependent operations.
 *
 * ## Why this hook exists
 *
 * Every feature that sends a transaction or signs a message needs to:
 * 1. Verify the correct wallet is connected (not just any wallet)
 * 2. Reconnect if it drifted (mobile sleep, extension restart)
 * 3. Handle user rejection, locked wallets, and mismatches consistently
 * 4. Update global signer status so the UI can reflect readiness
 *
 * Without this hook, each feature (claim rewards, swap, deposit, approve agent, sign typed data)
 * would implement its own reconnect/error handling, leading to inconsistent UX and duplicated code.
 *
 * ## How it works
 *
 * The guard provides two parallel paths — one for the main EVM wallet (Privy-authenticated)
 * and one for the linked Solana wallet (optional, linked separately):
 *
 * ### EVM path: `prepareMainEvmAction()` → `executeMainEvmAction(action)`
 * 1. Calls `ensureAuthenticatedWalletConnection()` to align wagmi with Privy
 * 2. Opens the Privy wallet dialog on disconnect/mismatch (fire-and-forget, never blocks)
 * 3. Updates `walletSignerStatusStore` so the header UI reflects signer state
 * 4. Wraps connection errors into `WalletActionError` with user-facing messages
 * 5. `executeMainEvmAction` runs the action, catches execution errors (locked, rejected),
 *    and optionally retries once on connector errors (safe for signatures, not for sends)
 *
 * ### Solana path: `prepareLinkedSolanaAction()` → `executeLinkedSolanaAction(action)`
 * 1. Checks if a Solana wallet is linked and connected via Privy
 * 2. Detects mismatch (different Solana wallet active than the one linked)
 * 3. Prompts linking if no Solana wallet exists
 * 4. Same error normalization as EVM path
 *
 * ## Error contract
 *
 * All errors thrown by this hook are `WalletActionError` instances with:
 * - `role`: which wallet path failed ("main-evm" | "linked-solana")
 * - `code`: one of WALLET_ACTION_ERROR_CODE values
 * - `message`: user-facing explanation
 * - `cause`: the original underlying error
 *
 * Consumers (approval connectors, claim flows, swap UI) catch these errors and
 * render contextual banners/messages based on the code.
 *
 * ## Consumers
 *
 * - `useSendTransactionWithConnection` — wraps wagmi sendTransaction (no retry)
 * - `useSignTypedDataWithConnection` — wraps wagmi signTypedData (retries once)
 * - `useSignMessageWithConnection` — wraps wagmi signMessage (retries once)
 * - `useClaimRewards` — uses prepareMainEvmAction for chain switch
 * - `SwapButton` — uses executeMainEvmAction for chain switch
 * - Hyperliquid/Paradex/GRVT/01.xyz approval connectors — use prepare or execute
 */
import { useCallback } from "react";
import { formatShortAddress } from "@cede/utils";
import {
  isConnectorConnectionError,
  isWalletConnectionError,
  useAuthenticatedWalletConnection,
  WalletConnectionError,
} from "./useAuthenticatedWalletConnection";
import { getWalletLockedMessage, isWalletLockedError, isWalletUserRejectedError } from "./walletInteractionErrors";
import { useSolanaWallet } from "@/hooks/solana/useSolanaWallet";
import { WALLET_SIGNER_STATUS, useWalletSignerStatusStore } from "@/store/walletSignerStatusStore";
import { usePrivy } from "@privy-io/react-auth";

/** The two wallet roles a user can act with. EVM is the auth identity; Solana is optional. */
export const WALLET_ACTION_ROLE = {
  MAIN_EVM: "main-evm",
  LINKED_SOLANA: "linked-solana",
} as const;

export type WalletActionRole = (typeof WALLET_ACTION_ROLE)[keyof typeof WALLET_ACTION_ROLE];

/**
 * All possible error codes from wallet actions. Superset of WALLET_CONNECTION_ERROR_CODE
 * plus "missing-wallet" (Solana not linked) and "user-rejected" (user declined in wallet).
 */
export const WALLET_ACTION_ERROR_CODE = {
  MISSING_WALLET: "missing-wallet",
  WALLET_NOT_AUTHENTICATED: "wallet-not-authenticated",
  USER_REJECTED: "user-rejected",
  WALLET_DISCONNECTED: "wallet-disconnected",
  WALLET_LOCKED: "wallet-locked",
  WALLET_MISMATCH: "wallet-mismatch",
  WALLET_CLIENT_UNAVAILABLE: "wallet-client-unavailable",
  WALLET_CONNECTION_INITIALIZING: "wallet-connection-initializing",
} as const;

export type WalletActionErrorCode = (typeof WALLET_ACTION_ERROR_CODE)[keyof typeof WALLET_ACTION_ERROR_CODE];

export class WalletActionError extends Error {
  code: WalletActionErrorCode;
  role: WalletActionRole;

  constructor(role: WalletActionRole, code: WalletActionErrorCode, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "WalletActionError";
    this.role = role;
    this.code = code;
  }
}

/**
 * All WalletConnectionError codes are a subset of WalletActionErrorCode,
 * so this is a type-checked passthrough. The exhaustive check ensures
 * new WalletConnectionError codes are noticed at compile time.
 */
const mapEvmConnectionErrorCode = (code: WalletConnectionError["code"]): WalletActionErrorCode => {
  // Exhaustiveness guard — if WalletConnectionErrorCode gains a variant
  // not present in WalletActionErrorCode, TypeScript will error here.
  const mapped: WalletActionErrorCode = code;
  return mapped;
};

export const isWalletActionError = (error: unknown): error is WalletActionError => error instanceof WalletActionError;

export const useWalletActionGuard = () => {
  const { ensureAuthenticatedWalletConnection, authenticatedWalletAddress, authenticatedWalletConnectorType } =
    useAuthenticatedWalletConnection();
  const { connectWallet } = usePrivy();
  const {
    solanaWallet,
    linkedSolanaAddress,
    activeInjectedWalletAddress,
    hasLinkedSolanaWallet,
    isWalletMismatch,
    connectAndLinkSolanaWallet,
  } = useSolanaWallet();
  const setMainEvmSignerStatus = useWalletSignerStatusStore((state) => state.setMainEvmSignerStatus);
  const setSolanaSignerStatus = useWalletSignerStatusStore((state) => state.setSolanaSignerStatus);

  /**
   * Opens the Privy wallet dialog prompting the user to reconnect.
   * Fire-and-forget — never awaited, never blocks the action pipeline.
   * If the user dismisses the dialog, the WalletActionError is the canonical signal.
   */
  const openAuthenticatedEvmWallet = useCallback(async () => {
    if (!authenticatedWalletAddress) return;

    try {
      await connectWallet({
        description: `Open ${formatShortAddress(authenticatedWalletAddress)}, the same wallet you used to sign in to Cede Hub.`,
      });
    } catch {
      // The follow-up WalletActionError remains the canonical UI signal.
    }
  }, [authenticatedWalletAddress, connectWallet]);

  /**
   * Validates the EVM wallet is connected and matches the authenticated identity.
   * Call this before any EVM transaction or signature. Returns { address, chainId }.
   * Throws WalletActionError on failure with a user-facing message.
   */
  const prepareMainEvmAction = useCallback(async () => {
    try {
      const connection = await ensureAuthenticatedWalletConnection();
      if (authenticatedWalletConnectorType === "wallet_connect") {
        // Fire-and-forget: nudge WalletConnect to surface the wallet app.
        // The actual wagmi send/sign call will prompt the wallet independently.
        openAuthenticatedEvmWallet();
      }
      setMainEvmSignerStatus(WALLET_SIGNER_STATUS.READY);

      return {
        address: authenticatedWalletAddress ?? connection.address,
        chainId: connection.chainId,
      };
    } catch (error) {
      setMainEvmSignerStatus(WALLET_SIGNER_STATUS.UNKNOWN);

      if (isWalletConnectionError(error)) {
        if (
          error.code === WALLET_ACTION_ERROR_CODE.WALLET_DISCONNECTED ||
          error.code === WALLET_ACTION_ERROR_CODE.WALLET_MISMATCH ||
          error.code === WALLET_ACTION_ERROR_CODE.WALLET_CLIENT_UNAVAILABLE
        ) {
          // Fire-and-forget: prompt the user to reconnect without blocking the error throw.
          // Awaiting here can hang the entire action pipeline if the Privy dialog is never
          // dismissed, which leaves the calling mutation in a permanent pending state and
          // silently disables the triggering button for all subsequent clicks.
          openAuthenticatedEvmWallet();
        }

        const actionMessage =
          error.code === WALLET_ACTION_ERROR_CODE.WALLET_CONNECTION_INITIALIZING
            ? error.message
            : error.code === WALLET_ACTION_ERROR_CODE.WALLET_LOCKED
              ? getWalletLockedMessage("main wallet")
              : error.code === WALLET_ACTION_ERROR_CODE.WALLET_MISMATCH
                ? "The connected wallet does not match the wallet you used to sign in. Open the same wallet and try again."
                : error.code === WALLET_ACTION_ERROR_CODE.WALLET_NOT_AUTHENTICATED
                  ? "Reconnect the wallet you used to sign in and try again."
                  : "Open the same wallet you used to sign in, then try again.";

        throw new WalletActionError(WALLET_ACTION_ROLE.MAIN_EVM, mapEvmConnectionErrorCode(error.code), actionMessage, {
          cause: error,
        });
      }

      throw error;
    }
  }, [
    authenticatedWalletAddress,
    authenticatedWalletConnectorType,
    ensureAuthenticatedWalletConnection,
    openAuthenticatedEvmWallet,
    setMainEvmSignerStatus,
  ]);

  /**
   * Normalizes execution errors (after the wallet action ran) into WalletActionError.
   * Detects locked-wallet and user-rejected patterns from raw wagmi/provider errors.
   * Re-throws unknown errors as-is for the caller to handle.
   */
  const handleMainEvmExecutionError = useCallback(
    (error: unknown): never => {
      if (isWalletActionError(error)) {
        throw error;
      }

      if (isWalletLockedError(error)) {
        setMainEvmSignerStatus(WALLET_SIGNER_STATUS.LOCKED);
        throw new WalletActionError(
          WALLET_ACTION_ROLE.MAIN_EVM,
          WALLET_ACTION_ERROR_CODE.WALLET_LOCKED,
          getWalletLockedMessage("main wallet"),
          {
            cause: error,
          },
        );
      }

      if (isWalletUserRejectedError(error)) {
        throw new WalletActionError(
          WALLET_ACTION_ROLE.MAIN_EVM,
          WALLET_ACTION_ERROR_CODE.USER_REJECTED,
          "Request rejected in wallet.",
          {
            cause: error,
          },
        );
      }

      throw error;
    },
    [setMainEvmSignerStatus],
  );

  /**
   * Full prepare → execute → error-handle pipeline for EVM wallet actions.
   * - Prepares the wallet connection (reconnect if needed)
   * - Runs the action callback
   * - Optionally retries once on connector errors (safe for signatures, NOT for sends)
   * - Normalizes all errors into WalletActionError
   */
  const executeMainEvmAction = useCallback(
    async <T>(action: () => Promise<T>, options?: { retryOnConnectorError?: boolean }): Promise<T> => {
      await prepareMainEvmAction();

      try {
        const result = await action();
        setMainEvmSignerStatus(WALLET_SIGNER_STATUS.READY);
        return result;
      } catch (error) {
        if (options?.retryOnConnectorError && isConnectorConnectionError(error)) {
          await prepareMainEvmAction();

          try {
            const retryResult = await action();
            setMainEvmSignerStatus(WALLET_SIGNER_STATUS.READY);
            return retryResult;
          } catch (retryError) {
            return handleMainEvmExecutionError(retryError);
          }
        }

        return handleMainEvmExecutionError(error);
      }
    },
    [handleMainEvmExecutionError, prepareMainEvmAction, setMainEvmSignerStatus],
  );

  /**
   * Validates the linked Solana wallet is connected and matches.
   * If no Solana wallet is linked, prompts the user to connect and link one.
   * Throws WalletActionError with role "linked-solana" on failure.
   */
  const prepareLinkedSolanaAction = useCallback(async () => {
    if (isWalletMismatch && linkedSolanaAddress) {
      setSolanaSignerStatus(WALLET_SIGNER_STATUS.UNKNOWN);
      throw new WalletActionError(
        WALLET_ACTION_ROLE.LINKED_SOLANA,
        WALLET_ACTION_ERROR_CODE.WALLET_MISMATCH,
        `The active Solana wallet does not match the linked wallet ${linkedSolanaAddress}.`,
      );
    }

    if (solanaWallet) {
      setSolanaSignerStatus(WALLET_SIGNER_STATUS.READY);
      return {
        address: solanaWallet.address,
        wallet: solanaWallet,
      };
    }

    setSolanaSignerStatus(WALLET_SIGNER_STATUS.UNKNOWN);
    await connectAndLinkSolanaWallet();

    throw new WalletActionError(
      WALLET_ACTION_ROLE.LINKED_SOLANA,
      hasLinkedSolanaWallet ? WALLET_ACTION_ERROR_CODE.WALLET_DISCONNECTED : WALLET_ACTION_ERROR_CODE.MISSING_WALLET,
      hasLinkedSolanaWallet
        ? "Your linked Solana wallet needs to be reopened. Please try again."
        : "Please connect your Solana wallet and try again.",
    );
  }, [
    connectAndLinkSolanaWallet,
    hasLinkedSolanaWallet,
    isWalletMismatch,
    linkedSolanaAddress,
    setSolanaSignerStatus,
    solanaWallet,
  ]);

  const handleLinkedSolanaExecutionError = useCallback(
    (error: unknown): never => {
      if (isWalletActionError(error)) {
        throw error;
      }

      if (isWalletLockedError(error)) {
        setSolanaSignerStatus(WALLET_SIGNER_STATUS.LOCKED);
        throw new WalletActionError(
          WALLET_ACTION_ROLE.LINKED_SOLANA,
          WALLET_ACTION_ERROR_CODE.WALLET_LOCKED,
          getWalletLockedMessage("Solana wallet"),
          {
            cause: error,
          },
        );
      }

      if (isWalletUserRejectedError(error)) {
        throw new WalletActionError(
          WALLET_ACTION_ROLE.LINKED_SOLANA,
          WALLET_ACTION_ERROR_CODE.USER_REJECTED,
          "Request rejected in wallet.",
          {
            cause: error,
          },
        );
      }

      throw error;
    },
    [setSolanaSignerStatus],
  );

  const executeLinkedSolanaAction = useCallback(
    async <T>(action: (wallet: NonNullable<typeof solanaWallet>) => Promise<T>): Promise<T> => {
      const prepared = await prepareLinkedSolanaAction();

      try {
        const result = await action(prepared.wallet);
        setSolanaSignerStatus(WALLET_SIGNER_STATUS.READY);
        return result;
      } catch (error) {
        return handleLinkedSolanaExecutionError(error);
      }
    },
    [handleLinkedSolanaExecutionError, prepareLinkedSolanaAction, setSolanaSignerStatus, solanaWallet],
  );

  return {
    authenticatedWalletAddress,
    linkedSolanaAddress,
    activeInjectedWalletAddress,
    prepareMainEvmAction,
    executeMainEvmAction,
    prepareLinkedSolanaAction,
    executeLinkedSolanaAction,
    handleMainEvmExecutionError,
    handleLinkedSolanaExecutionError,
  };
};
